// ================ Action types ================ //

import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { resultIds } from '../../util/listings';
import { listingTypes as purchaseListingTypes } from '../../transactions/transactionProcessPurchase';

// ================ Constants ================ //
export const MULTI_CTA_SECTION_ID = 'multi-cta-hero';
export const LISTING_CATEGORIES_SECTION_ID = 'listing-categories';
export const LISTING_TABS_SECTION_ID = 'listing-tabs';
export const NEWEST_SECTION_ID = 'newest-listings';
export const RECOMMENDED_SECTION_ID = 'recommended-listings';
export const TAB_SECTION_ID = 'section-tab';
export const TABS_SECTION_ID = 'section-tabs';
export const CONTACT_SECTION_ID = 'section-contact';
export const HEADING_SECTION_ID = 'section-heading';
export const BREADRCUMBS_SECTION_ID = 'section-breadcrumbs';

// ================ Action types ================ //
export const FETCH_LISTING_TAB_REQUEST = 'app/PageBuilder/FETCH_LISTING_TAB_REQUEST';
export const FETCH_LISTING_TAB_SUCCESS = 'app/PageBuilder/FETCH_LISTING_TAB_SUCCESS';
export const FETCH_LISTING_TAB_ERROR = 'app/PageBuilder/FETCH_LISTING_TAB_ERROR';

export const FETCH_NEWEST_LISTINGS_REQUEST = 'app/PageBuilder/FETCH_NEWEST_LISTINGS_REQUEST';
export const FETCH_NEWEST_LISTINGS_SUCCESS = 'app/PageBuilder/FETCH_NEWEST_LISTINGS_SUCCESS';
export const FETCH_NEWEST_LISTINGS_ERROR = 'app/PageBuilder/FETCH_NEWEST_LISTINGS_ERROR';

export const FETCH_RECOMMENDED_LISTINGS_REQUEST =
  'app/PageBuilder/FETCH_RECOMMENDED_LISTINGS_REQUEST';
export const FETCH_RECOMMENDED_LISTINGS_SUCCESS =
  'app/PageBuilder/FETCH_RECOMMENDED_LISTINGS_SUCCESS';
export const FETCH_RECOMMENDED_LISTINGS_ERROR = 'app/PageBuilder/FETCH_RECOMMENDED_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  listingTabs: {},
  newestListing: {
    inProgress: false,
    error: null,
    resultIds: [],
  },
  recommendedListing: {
    inProgress: false,
    error: null,
    resultIds: [],
  },
};

const listingTabDefaultState = {
  searchParams: '',
  inProgress: false,
  succeed: false,
  resultsIds: [],
  error: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTING_TAB_REQUEST:
      return {
        ...state,
        listingTabs: {
          ...state.listingTabs,
          [payload.searchHref]: {
            searchParams: payload.searchParams,
            inProgress: true,
            succeed: false,
            resultsIds: [],
            error: null,
          },
        },
      };
    case FETCH_LISTING_TAB_SUCCESS:
      return {
        ...state,
        listingTabs: {
          ...state.listingTabs,
          [payload.searchHref]: {
            inProgress: false,
            succeed: true,
            resultsIds: resultIds(payload.data),
          },
        },
      };
    case FETCH_LISTING_TAB_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload.error);

      return {
        ...state,
        listingTabs: {
          ...state.listingTabs,
          [payload.searchHref]: {
            inProgress: false,
            error: payload.error,
          },
        },
      };

    case FETCH_NEWEST_LISTINGS_REQUEST:
      return {
        ...state,
        newestListing: {
          resultIds: [],
          inProgress: true,
          error: null,
        },
      };
    case FETCH_NEWEST_LISTINGS_SUCCESS:
      return {
        ...state,
        newestListing: {
          resultIds: payload.ids,
          inProgress: false,
          error: null,
        },
      };
    case FETCH_NEWEST_LISTINGS_ERROR:
      return {
        ...state,
        newestListing: {
          resultIds: [],
          inProgress: false,
          error: payload.error,
        },
      };

    case FETCH_RECOMMENDED_LISTINGS_REQUEST:
      return {
        ...state,
        recommendedListing: {
          resultIds: [],
          inProgress: true,
          error: null,
        },
      };
    case FETCH_RECOMMENDED_LISTINGS_SUCCESS:
      return {
        ...state,
        recommendedListing: {
          resultIds: payload.ids,
          inProgress: false,
          error: null,
        },
      };
    case FETCH_RECOMMENDED_LISTINGS_ERROR:
      return {
        ...state,
        recommendedListing: {
          resultIds: [],
          inProgress: false,
          error: payload.error,
        },
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const listingTabSelector = searchHref => state => {
  return state.PageBuilder.listingTabs[searchHref] || listingTabDefaultState;
};

export const listingTabDataSelector = searchHref => state => {
  const { resultsIds, inProgress, error } = listingTabSelector(searchHref)(state);

  return {
    data: getListingsById(state, resultsIds),
    inProgress,
    error,
  };
};

export const listingTabRequestedSelector = searchHref => state => {
  const { inProgress, error, succeed } = listingTabSelector(searchHref)(state);

  return !inProgress && !error && succeed;
};

// ================ Action creators ================ //

export const fetchListingTabRequest = (searchHref, searchParams) => ({
  type: FETCH_LISTING_TAB_REQUEST,
  payload: { searchHref, searchParams },
});

export const fetchListingTabSuccess = (response, searchHref) => ({
  type: FETCH_LISTING_TAB_SUCCESS,
  payload: { searchHref, data: response.data },
});

export const fetchListingTabError = (searchHref, error) => ({
  type: FETCH_LISTING_TAB_ERROR,
  payload: { searchHref, error },
  error: true,
});

export const fetchNewestListingsRequest = () => ({ type: FETCH_NEWEST_LISTINGS_REQUEST });

export const fetchNewestListingsSuccess = response => ({
  type: FETCH_NEWEST_LISTINGS_SUCCESS,
  payload: { ids: resultIds(response.data) },
});

export const fetchNewestListingsError = error => ({
  type: FETCH_NEWEST_LISTINGS_ERROR,
  payload: { error },
});

export const fetchRecommendedListingsRequest = () => ({ type: FETCH_RECOMMENDED_LISTINGS_REQUEST });

export const setRecommendedListingsSuccess = ids => ({
  type: FETCH_RECOMMENDED_LISTINGS_SUCCESS,
  payload: { ids },
});

export const fetchRecommendedListingsSuccess = response => ({
  type: FETCH_RECOMMENDED_LISTINGS_SUCCESS,
  payload: { ids: resultIds(response.data) },
});

export const fetchRecommendedListingsError = error => ({
  type: FETCH_RECOMMENDED_LISTINGS_ERROR,
  payload: { error },
});

// ================ Thunks ================ //

export const fetchListingTab = (config, searchHref) => async (dispatch, getState, sdk) => {
  const searchStr = searchHref?.indexOf('?') >= 0 ? searchHref.split('?').pop() : '';
  const searchParams = parse(searchStr);
  const alreadyRequested = listingTabRequestedSelector(searchHref)(getState());

  if (alreadyRequested) {
    return;
  }
  try {
    dispatch(fetchListingTabRequest(searchHref, searchParams));
    const response = await dispatch(fetchListings(config, searchParams));
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchListingTabSuccess(response, searchHref));
  } catch (e) {
    dispatch(fetchListingTabError(searchHref, storableError(e)));
    throw e;
  }
};

export const fetchNewestListings = (config, params) => async (dispatch, getState, sdk) => {
  try {
    dispatch(fetchNewestListingsRequest());
    const response = await dispatch(fetchListings(config, { sort: 'createdAt' }));
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchNewestListingsSuccess(response));
  } catch (e) {
    dispatch(fetchNewestListingsError(storableError(e)));
    throw e;
  }
};

export const fetchRecommendedListings = (config, recommendedListingIds) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(fetchRecommendedListingsRequest());
    const response = await dispatch(fetchListings(config, { ids: recommendedListingIds }));
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchRecommendedListingsSuccess(response));
  } catch (e) {
    dispatch(fetchRecommendedListingsError(storableError(e)));
    throw e;
  }
};

export const fetchListings = (config, additionalParams = {}) => async (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return await sdk.listings.query({
    page: 1,
    perPage: 16,
    ...additionalParams,
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'metadata.wishlistCount',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.color',
      'publicData.size',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    'fields.image': [
      'variants.scaled-small',
      'variants.square-small2x',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    // Ensure that the listing is instock
    minStock: 1,
    stockMode: 'match-undefined',
    pub_listingType: purchaseListingTypes,
  });
};

export const loadSectionData = (section, params, search, config) => async (
  dispatch,
  getState,
  sdk
) => {
  const { sectionId } = section;

  switch (sectionId) {
    case LISTING_TABS_SECTION_ID: {
      if (!section.blocks?.length) {
        return;
      }

      const { href } = section.blocks[0].callToAction || {};
      await dispatch(fetchListingTab(config, href));
      break;
    }

    case NEWEST_SECTION_ID: {
      await dispatch(fetchNewestListings(config));
      break;
    }

    case RECOMMENDED_SECTION_ID: {
      if (!section.blocks?.length) {
        return;
      }
      const recommendedListingIds = section?.blocks.map(b => b.blockName) || [];
      // Set the recommended listing ids to the store, it's needed for getListingsById func
      setRecommendedListingsSuccess(recommendedListingIds);
      await dispatch(fetchRecommendedListings(config, recommendedListingIds));
      break;
    }

    default:
      break;
  }
};

export const withPageBuilder = handler => (params, search, config) => async dispatch => {
  const pageAssets = await dispatch(handler(params, search, config));

  const sections = Object.entries(pageAssets).reduce((reduced, [, { data } = {}]) => {
    const { sections = [] } = data || {};
    return [...reduced, ...sections];
  }, []);

  await Promise.all(
    sections.map(section => dispatch(loadSectionData(section, params, search, config)))
  );

  return pageAssets;
};
